import { useState, useEffect, useMemo } from 'react'
import { getTimeOff, getEventos, getCumpleanos } from '../../api/client'
import EventosModal from './EventosModal'
import { toISODate } from '../../utils/weeks'
import { TIPO_CELDA, rangoFechas } from '../../utils/ausencias'

// Personas > Calendario: one month, a row per person and a column per day,
// coloured by who is out -- vacations, días administrativos, licences,
// permissions, work trips. Everyone sees who and when (the notes are only
// sent to whoever may read them). Pending requests are striped (not leave
// yet), half days fill half a cell, weekends and holidays are shaded.
// With `ventana` (n days) it is the directory's strip instead: today and the
// next n days, only who is away, in `areaFija` (the directory's area filter;
// '' = everyone), no controls of its own -- `onVerMas` opens the full tab.
//
//   Mi equipo = everyone below me in the reporting line (supervisor_id), at
//   any depth, and everyone in the areas I head (es_jefe) and those below
//   them -- the same chain that approves their leave (backend ausencias.py).

const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

function diasDelMes(anio, mes) {
  const out = []
  const d = new Date(anio, mes, 1)
  while (d.getMonth() === mes) { out.push(toISODate(d)); d.setDate(d.getDate() + 1) }
  return out
}

// Who reports to `miId`, directly or further down, plus whoever is in an
// area `miId` heads or any area below it (`subarbol`: code -> its codes).
function miEquipo(personas, miId, subarbol) {
  const hijos = new Map()
  for (const p of personas) {
    if (p.supervisor_id == null) continue
    if (!hijos.has(p.supervisor_id)) hijos.set(p.supervisor_id, [])
    hijos.get(p.supervisor_id).push(p.id)
  }
  const out = new Set()
  const yo = personas.find(p => p.id === miId)
  const dirijo = new Set()
  for (const a of yo?.areas || []) if (a.es_jefe) for (const c of subarbol(a.code)) dirijo.add(c)
  const pila = [...(hijos.get(miId) || [])]
  for (const p of personas) if (p.id !== miId && p.areas.some(a => dirijo.has(a.code))) pila.push(p.id)
  while (pila.length) {
    const id = pila.pop()
    if (out.has(id)) continue
    out.add(id)
    pila.push(...(hijos.get(id) || []))
  }
  return out
}

// A day's share of a leave row: 'full' | 'am' | 'pm' (which half is off),
// as backend availability.day_fraction.
function parteDelDia(a, dia) {
  if (!a.half_day) return 'full'
  if (a.start_date === a.end_date) return a.mitad === 'pm' ? 'pm' : 'am'
  if (a.mitad === 'pm') return dia === a.start_date ? 'pm' : 'full'
  return dia === a.end_date ? 'am' : 'full'
}

function estiloCelda(a, parte) {
  const color = TIPO_CELDA[a.type] || '#d1d5db'
  if (a.estado === 'pendiente') {
    const rayas = `repeating-linear-gradient(135deg, ${color} 0 3px, transparent 3px 6px)`
    if (parte === 'full') return { backgroundImage: rayas }
    return { backgroundImage: `linear-gradient(to ${parte === 'am' ? 'right' : 'left'}, transparent 50%, white 50%), ${rayas}` }
  }
  if (parte === 'full') return { background: color }
  return { background: `linear-gradient(to ${parte === 'am' ? 'right' : 'left'}, ${color} 50%, transparent 50%)` }
}

export default function Calendario({ t, personas, areas, subarbol, miId, feriados, ventana, areaFija = '', onVerMas,
                                     puedeGestionarEventos = false }) {
  const hoy = toISODate(new Date())
  const [mes, setMes] = useState(() => { const d = new Date(); return { anio: d.getFullYear(), mes: d.getMonth() } })
  const [filtro, setFiltro] = useState(miId != null ? 'equipo' : 'todos')   // equipo | todos | <area code>
  const [soloAusentes, setSoloAusentes] = useState(true)
  const [q, setQ] = useState('')
  const [filas, setFilas] = useState(null)
  const [error, setError] = useState('')
  const [eventos, setEventos] = useState([])          // company events in range (Navidad, paseo...)
  const [cumples, setCumples] = useState([])          // birthdays in range: [{ id, name, fecha }]
  const [verEventos, setVerEventos] = useState(false)
  const [version, setVersion] = useState(0)           // bumped when events change

  const compacto = ventana != null
  const dias = useMemo(() => {
    if (!compacto) return diasDelMes(mes.anio, mes.mes)
    const out = [], d = new Date(hoy + 'T00:00:00')
    for (let i = 0; i <= ventana; i++) { out.push(toISODate(d)); d.setDate(d.getDate() + 1) }
    return out
  }, [mes, compacto, ventana, hoy])
  const desde = dias[0], hasta = dias[dias.length - 1]

  useEffect(() => {
    let vivo = true
    setFilas(null); setError('')
    getTimeOff({ desde, hasta })
      .then(rs => { if (vivo) setFilas(rs.filter(r => r.estado === 'aprobada' || r.estado === 'pendiente')) })
      .catch(() => { if (vivo) { setFilas([]); setError(t('calendario.loadFailed')) } })
    getEventos({ desde, hasta }).then(es => { if (vivo) setEventos(es) }).catch(() => {})
    getCumpleanos(desde, hasta).then(cs => { if (vivo) setCumples(cs) }).catch(() => {})
    return () => { vivo = false }
  }, [desde, hasta, t, version])

  const equipo = useMemo(() => miEquipo(personas || [], miId, subarbol), [personas, miId, subarbol])
  const feriado = useMemo(() => new Map((feriados || []).map(f => [f.date, f.name])), [feriados])

  const porPersona = useMemo(() => {
    const m = new Map()
    for (const r of filas || []) {
      if (!m.has(r.team_member_id)) m.set(r.team_member_id, [])
      m.get(r.team_member_id).push(r)
    }
    return m
  }, [filas])

  const visibles = useMemo(() => {
    const n = compacto ? '' : norm(q.trim())
    const f = compacto ? (areaFija || 'todos') : filtro
    return (personas || [])
      .filter(p => f === 'todos' || (f === 'equipo' ? equipo.has(p.id) || p.id === miId
        : p.areas.some(a => subarbol(f).has(a.code))))
      .filter(p => !(compacto || soloAusentes) || porPersona.has(p.id))
      .filter(p => !n || norm(p.name).includes(n))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [personas, filtro, equipo, miId, soloAusentes, porPersona, q, subarbol, compacto, areaFija])

  const mover = (n) => setMes(m => {
    const d = new Date(m.anio, m.mes + n, 1)
    return { anio: d.getFullYear(), mes: d.getMonth() }
  })
  const titulo = new Date(mes.anio, mes.mes, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

  const eventosDe = (dia) => eventos.filter(e => e.start_date <= dia && e.end_date >= dia)
  // A company day off (whole days only) is off for everyone, like a holiday.
  const eventoLibre = (dia) => eventosDe(dia).some(e => e.libre && !e.mitad)
  const libre = (dia) => [0, 6].includes(new Date(dia + 'T00:00:00').getDay()) || feriado.has(dia) || eventoLibre(dia)
  // The leave covering this day (approved over pending when two do), if any.
  const deDia = (p, dia) => {
    if (!dia || libre(dia)) return null
    const suyas = (porPersona.get(p.id) || []).filter(a => a.start_date <= dia && a.end_date >= dia)
    return suyas.find(x => x.estado === 'aprobada') || suyas[0] || null
  }
  // A day's column: holidays a soft amber (off for everyone), weekends grey,
  // today tinted, a line where a week starts.
  const columna = (dia, i) => [
    feriado.has(dia) ? 'bg-amber-50' : eventoLibre(dia) ? 'bg-violet-50' : libre(dia) ? 'bg-gray-50' : '',
    dia === hoy && !libre(dia) ? 'bg-sky-50' : '',
    i > 0 && new Date(dia + 'T00:00:00').getDay() === 1 ? 'border-l border-gray-200' : '',
  ].join(' ')

  const celda = (p, dia, i) => {
    const a = deDia(p, dia)
    if (!a) {
      return <td key={dia} title={feriado.get(dia) || undefined} className={`h-8 ${columna(dia, i)}`} />
    }
    const parte = parteDelDia(a, dia)
    // One bar per absence: square where it goes on from the day before/after.
    const sigue = (otro) => { const b = deDia(p, otro); return b != null && b.id === a.id }
    const izq = parte !== 'pm' && sigue(dias[i - 1]) ? '' : 'rounded-l-md'
    const der = parte !== 'am' && sigue(dias[i + 1]) ? '' : 'rounded-r-md'
    const texto = [
      p.name, t(`tipos.${a.type}`, a.type), rangoFechas(a.start_date, a.end_date),
      parte !== 'full' ? t(`calendario.${parte}`) : null,
      a.estado === 'pendiente' ? t('estados.pendiente') : null,
      a.origen === 'buk' ? 'Buk' : null, a.notes ? `“${a.notes}”` : null,
    ].filter(Boolean).join(' · ')
    return (
      <td key={dia} title={texto} className={`h-8 py-1 ${columna(dia, i)}`}>
        <div className={`h-5 ${izq} ${der} ${izq ? 'ml-0.5' : ''} ${der ? 'mr-0.5' : ''}`} style={estiloCelda(a, parte)} />
      </td>
    )
  }

  return (
    <div className={compacto ? 'bg-white rounded-xl border border-gray-200 p-5 mb-6' : ''}>
      {compacto ? (
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-gray-800">
            {t('ausentes.titulo')} <span className="font-normal text-xs text-gray-400">· {t('ausentes.ventana', { count: ventana })}</span>
          </h2>
          {onVerMas && (
            <button onClick={onVerMas} className="text-xs font-medium text-gray-500 hover:text-gray-900">
              {t('ausentes.verCalendario')} →
            </button>
          )}
        </div>
      ) : (
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white">
          <button onClick={() => mover(-1)} className="px-2.5 py-1.5 text-gray-500 hover:text-gray-900">‹</button>
          <span className="px-2 text-sm font-medium text-gray-900 capitalize min-w-36 text-center">{titulo}</span>
          <button onClick={() => mover(1)} className="px-2.5 py-1.5 text-gray-500 hover:text-gray-900">›</button>
        </div>
        <select value={filtro} onChange={e => setFiltro(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          {miId != null && <option value="equipo">{t('calendario.miEquipo')}</option>}
          <option value="todos">{t('calendario.todos')}</option>
          {areas.map(a => <option key={a.code} value={a.code}>{'   '.repeat(a.nivel || 0)}{a.name}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('buscar')}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-56 bg-white" />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={soloAusentes} onChange={e => setSoloAusentes(e.target.checked)} className="accent-gray-900" />
          {t('calendario.soloAusentes')}
        </label>
        {puedeGestionarEventos && (
          <button onClick={() => setVerEventos(true)}
            className="ml-auto border border-violet-200 text-violet-700 bg-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-violet-50">
            {t('eventos.boton')}
          </button>
        )}
      </div>
      )}
      {verEventos && (
        <EventosModal t={t} onClose={() => setVerEventos(false)} onCambio={() => setVersion(v => v + 1)} />
      )}

      <div className="flex flex-wrap gap-3 mb-3 text-xs text-gray-500">
        {Object.entries(TIPO_CELDA).map(([tipo, color]) => (
          <span key={tipo} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: color }} />{t(`tipos.${tipo}`, tipo)}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundImage: 'repeating-linear-gradient(135deg, #9ca3af 0 2px, transparent 2px 4px)' }} />
          {t('estados.pendiente')}
        </span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-50 border border-amber-200" />{t('calendario.feriado')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-gray-50 border border-gray-200" />{t('calendario.finDeSemana')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-300" />{t('eventos.leyendaLibre')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white border border-violet-300" />{t('eventos.leyendaActividad')}</span>
      </div>

      {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg mb-4">{error}</p>}

      {filas === null ? (
        <div className="text-gray-400 text-sm">{t('cargando')}</div>
      ) : visibles.length === 0 && !eventos.length && !cumples.length ? (
        compacto
          ? <p className="text-sm text-gray-400">{t('ausentes.nadie', { count: ventana })}</p>
          : <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-xl text-sm">{t('calendario.nadie')}</div>
      ) : (
        // Full width: the name column is fixed, the days share the rest (and
        // scroll sideways only when each would get under ~26px).
        <div className={`bg-white overflow-x-auto ${compacto ? '' : 'rounded-xl border border-gray-200'}`}>
          <table className="w-full table-fixed border-collapse text-xs"
            style={{ minWidth: `${(compacto ? 11 : 13) * 16 + dias.length * 26}px` }}>
            <colgroup>
              <col style={{ width: compacto ? '11rem' : '13rem' }} />
              {dias.map(dia => <col key={dia} />)}
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200">
                <th className="sticky left-0 bg-white z-10" />
                {dias.map((dia, i) => {
                  const d = new Date(dia + 'T00:00:00')
                  const esHoy = dia === hoy
                  return (
                    <th key={dia} title={[feriado.get(dia), ...eventosDe(dia).map(e => e.nombre)].filter(Boolean).join(' · ') || undefined}
                      className={`py-1.5 font-medium ${columna(dia, i)} ${
                        feriado.has(dia) ? 'text-amber-600' : eventosDe(dia).length ? 'text-violet-600'
                          : libre(dia) ? 'text-gray-300' : 'text-gray-500'}`}>
                      <div className="text-[9px] uppercase tracking-wide">{d.toLocaleDateString(undefined, { weekday: 'narrow' })}</div>
                      <div className={`mx-auto mt-0.5 w-6 h-6 leading-6 rounded-full tabular-nums ${esHoy ? 'bg-gray-900 text-white' : ''}`}>
                        {d.getDate()}
                      </div>
                      <div className={`mx-auto mt-0.5 w-1 h-1 rounded-full ${feriado.has(dia) ? 'bg-amber-400'
                        : eventosDe(dia).length ? 'bg-violet-400' : ''}`} />
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {/* The company's own events, above everyone. */}
              {eventos.length > 0 && (
                <tr className="border-b border-gray-200">
                  <td className={`sticky left-0 bg-white z-10 ${compacto ? 'pl-1' : 'pl-4'} pr-3 text-[11px] font-semibold uppercase tracking-wide text-violet-600`}>
                    {t('eventos.fila')}
                  </td>
                  {dias.map((dia, i) => {
                    const e = eventosDe(dia)[0]
                    if (!e) return <td key={dia} className={`h-8 ${columna(dia, i)}`} />
                    const sigue = (otro) => !!otro && eventosDe(otro)[0]?.id === e.id
                    const izq = sigue(dias[i - 1]) ? '' : 'rounded-l-md ml-0.5'
                    const der = sigue(dias[i + 1]) ? '' : 'rounded-r-md mr-0.5'
                    const medio = e.mitad ? (e.mitad === 'am' ? 'w-1/2' : 'w-1/2 ml-auto') : ''
                    const texto = [e.nombre, rangoFechas(e.start_date, e.end_date),
                      e.mitad ? t(`pedir.jornada.${e.mitad}`) : null,
                      e.libre ? t('eventos.esLibre') : t('eventos.esActividad'), e.notas].filter(Boolean).join(' · ')
                    return (
                      <td key={dia} className={`h-8 py-1 ${columna(dia, i)}`} title={texto}>
                        <div className={`h-5 ${izq} ${der} ${medio} ${e.libre ? 'bg-violet-300' : 'bg-white border border-violet-300'}`} />
                      </td>
                    )
                  })}
                </tr>
              )}
              {/* Birthdays: a cake on the day, who on hover (day and month only). */}
              {cumples.length > 0 && (
                <tr className="border-b border-gray-200">
                  <td className={`sticky left-0 bg-white z-10 ${compacto ? 'pl-1' : 'pl-4'} pr-3 text-[11px] font-semibold uppercase tracking-wide text-pink-600`}>
                    {t('calendario.cumpleanos')}
                  </td>
                  {dias.map((dia, i) => {
                    const quienes = cumples.filter(c => c.fecha === dia)
                    return (
                      <td key={dia} className={`h-8 text-center ${columna(dia, i)}`}
                        title={quienes.length ? `${t('calendario.cumpleDe')}: ${quienes.map(c => c.name).join(', ')}` : undefined}>
                        {quienes.length > 0 && (
                          <span className="inline-flex items-center text-sm leading-none">
                            🎂{quienes.length > 1 && <sup className="text-[9px] font-semibold text-pink-600 ml-0.5">{quienes.length}</sup>}
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )}
              {visibles.map(p => (
                <tr key={p.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60">
                  <td className={`sticky left-0 bg-white z-10 ${compacto ? "pl-1" : "pl-4"} pr-3 truncate ${p.id === miId ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
                    title={p.name}>
                    {p.name}
                  </td>
                  {dias.map((dia, i) => celda(p, dia, i))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
